/**
 * LilliChat.jsx — internal Lilli AI chat panel.
 *
 * Mounted next to LilliButton. Slides up from the bottom-right launcher.
 * Talks to POST /api/lilli/chat and reads history from GET /api/lilli/messages.
 *
 * Supports lightweight markdown and voice input. Auto-clears server-side
 * after 72h; the user can also click "Clear chat" to wipe their history.
 */
import React, { useEffect, useRef, useState } from 'react'
import { X, Send, Trash2, Loader2, Sparkles, Mic, ShieldAlert, Info } from 'lucide-react'
import api from '../services/api'
import { getLilliPageContext, useLilliPageContext, lilliContextLabel } from '../lib/lilliPageContext'

const COPPER = '#cfa190'
const DARK   = '#1e2a2e'
const SEND_TIMEOUT_MS = 60_000
const VOICE_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/aac', 'audio/ogg;codecs=opus']

function createVoiceRecorder(stream) {
  for (const mimeType of VOICE_MIME_TYPES) {
    try {
      if (MediaRecorder.isTypeSupported?.(mimeType)) {
        const recorder = new MediaRecorder(stream, { mimeType })
        return { recorder, mimeType: recorder.mimeType || mimeType }
      }
    } catch {}
  }
  const recorder = new MediaRecorder(stream)
  return { recorder, mimeType: recorder.mimeType || '' }
}

function voiceExtensionForMime(mimeType) {
  const base = String(mimeType || '').split(';')[0].toLowerCase()
  if (base === 'audio/mp4') return '.mp4'
  if (base === 'audio/aac') return '.m4a'
  if (base === 'audio/ogg') return '.ogg'
  return '.webm'
}

function formatTime(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[ch]))
}

function safeHttpUrl(value) {
  try {
    const url = new URL(String(value || '').trim())
    return ['http:', 'https:'].includes(url.protocol) ? url.href : null
  } catch {
    return null
  }
}

function renderLilliMarkdown(text) {
  const source = String(text ?? '')

  let html = escapeHtml(source)
  html = html.replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, '<em>$1</em>')
  html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
    (_, label, url) => {
      const safeUrl = safeHttpUrl(url)
      return safeUrl
        ? `<a href="${escapeHtml(safeUrl)}" target="_blank" rel="noopener noreferrer" class="underline">${label}</a>`
        : label
    })
  html = html.replace(/`([^`]+)`/g, '<code class="bg-black/10 dark:bg-white/10 px-1 rounded text-xs">$1</code>')
  html = html.replace(/\n/g, '<br>')

  return <span dangerouslySetInnerHTML={{ __html: html }} />
}

// Phase 2C — page context sent to /api/lilli/ask: the registered page context (selected
// thread / villa / lead / booking, set by ThreadReader, VillaDetail, LeadDetail via
// lilliPageContext) merged over a route-derived baseline. Never blocks when something is
// unavailable (the backend reads role from the JWT, and missing entities come back as
// missingFacts instead of guesses).
function buildPageContext() {
  const pathname = (typeof window !== 'undefined' && window.location?.pathname) || ''
  const ctx = { page: pathname || 'app', pathname }
  const villaMatch = pathname.match(/\/villas?\/(\d+)/)
  if (villaMatch) ctx.selectedVillaId = villaMatch[1]
  return { ...ctx, ...getLilliPageContext() }
}

// Phase 2B — compact verified-answer metadata under a Lilli Core reply: approval-required
// badge, gold warnings, missing facts, the tools/sources used, and a quiet audit id.
function LilliMeta({ meta }) {
  if (!meta) return null
  const { sourcesUsed = [], warnings = [], missingFacts = [], requiresApproval, auditId } = meta
  if (!requiresApproval && !sourcesUsed.length && !warnings.length && !missingFacts.length && !auditId) return null
  return (
    <div className="mt-2 space-y-1.5">
      {requiresApproval && (
        <div className="flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-md bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
          <ShieldAlert size={12} className="flex-shrink-0" /> Approval required — Lilli won’t perform this action.
        </div>
      )}
      {warnings.map((w, i) => (
        <div key={`w${i}`} className="flex items-start gap-1.5 text-[11px] px-2 py-1 rounded-md border" style={{ background: 'rgba(207,161,144,0.12)', borderColor: 'rgba(207,161,144,0.35)', color: '#8a6a5b' }}>
          <span aria-hidden className="flex-shrink-0">⚠</span><span>{w}</span>
        </div>
      ))}
      {missingFacts.length > 0 && (
        <div className="flex items-start gap-1.5 text-[11px] px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
          <Info size={12} className="mt-px flex-shrink-0" />
          <span><span className="font-medium">Missing facts:</span> {missingFacts.join(' · ')}</span>
        </div>
      )}
      {sourcesUsed.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400">
          <span className="opacity-70">Sources:</span>
          {sourcesUsed.map((s, i) => (
            <span key={`s${i}`} title={s.label || ''} className="px-1.5 py-0.5 rounded bg-slate-200/70 dark:bg-slate-700/60">
              {s.tool}{s.status && s.status !== 'verified' ? ` · ${s.status}` : ''}
            </span>
          ))}
        </div>
      )}
      {auditId && <div className="text-[9px] text-slate-400 dark:text-slate-500 font-mono opacity-70">{auditId}</div>}
    </div>
  )
}

// Phase 2D — safe-action whitelist. ONLY these action types render; anything else from
// the backend (or a compromised response) is silently dropped. None of these executes a
// production change: they open existing reviewed flows, copy text, or ask a read-only
// follow-up question.
const SAFE_LILLI_ACTIONS = new Set([
  'open_draft_reply',
  'open_draft_quote',
  'prepare_wordpress_draft',
  'suggest_follow_up',
  'run_data_quality_check',
  'copy_answer',
  'open_related_record',
])

// Compact action buttons under an assistant answer. Approval-gated actions get amber
// styling; safe ones the copper outline. A Copy button is always appended client-side.
function LilliActions({ actions, message, onAction }) {
  const [feedback, setFeedback] = useState(null) // index → short confirmation label
  const list = [
    ...(actions || []).filter(a => a && SAFE_LILLI_ACTIONS.has(a.type)),
    { type: 'copy_answer', label: 'Copy answer', safe: true, requiresApproval: false },
  ]
  if (!list.length) return null
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {list.map((a, i) => (
        <button
          key={`${a.type}-${i}`}
          type="button"
          title={a.description || a.label}
          onClick={async () => {
            const note = await onAction(a, message)
            if (note) { setFeedback({ i, note }); setTimeout(() => setFeedback(null), 2000) }
          }}
          className={`text-[11px] px-2.5 py-1 rounded-md border font-medium transition ${
            a.requiresApproval
              ? 'border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-300 dark:bg-amber-950/40'
              : 'border-[#cfa190]/60 text-[#8a6a5b] hover:bg-[#cfa190]/10 dark:text-[#cfa190]'
          }`}
        >
          {feedback?.i === i ? feedback.note : a.label}{a.requiresApproval ? ' · approval' : ''}
        </button>
      ))}
    </div>
  )
}

export default function LilliChat({ open, onClose, onUnreadChange }) {
  const pageCtx = useLilliPageContext() // reactive — the header hint follows the page
  const ctxLabel = lilliContextLabel(pageCtx)
  const [messages, setMessages] = useState([])
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError]   = useState(null)
  const [abortedDraft, setAbortedDraft] = useState(null)
  const [recording, setRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const scrollerRef = useRef(null)
  const inputRef    = useRef(null)
  const sendAbortRef = useRef(null)
  const recorderRef = useRef(null)
  const chunksRef = useRef([])
  const streamRef = useRef(null)
  const recordTimerRef = useRef(null)
  const recordSecondsRef = useRef(0)

  // Load history when the panel opens.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api.get('/lilli/messages')
      .then(({ data }) => {
        if (cancelled) return
        setMessages(Array.isArray(data?.messages) ? data.messages : [])
      })
      .catch(err => {
        if (cancelled) return
        const status = err?.response?.status
        if (status === 403) {
          setError("Internal Lilli isn't enabled for your account.")
        } else {
          setError(err?.response?.data?.error || err.message || 'Failed to load history')
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open])

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    if (!scrollerRef.current) return
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight
  }, [messages, open, sending])

  // Focus the composer when opening.
  useEffect(() => {
    if (open) {
      // Tiny delay so the panel transition doesn't steal focus.
      const t = setTimeout(() => inputRef.current?.focus(), 80)
      return () => clearTimeout(t)
    }
  }, [open])

  useEffect(() => {
    return () => {
      sendAbortRef.current?.abort()
      streamRef.current?.getTracks?.().forEach(t => t.stop())
      clearInterval(recordTimerRef.current)
    }
  }, [])

  // Clear unread indicator when the panel is open.
  useEffect(() => {
    if (open && onUnreadChange) onUnreadChange(false)
  }, [open, onUnreadChange])

  async function handleSend(e) {
    e?.preventDefault?.()
    const text = draft.trim()
    if (!text || sending) return
    setDraft('')
    await sendQuestion(text)
  }

  // Core ask flow — used by the composer AND by safe actions that trigger a follow-up
  // question (e.g. run_data_quality_check). Read-only; never sends email.
  async function sendQuestion(text) {
    if (!text || sending) return
    setSending(true)
    setError(null)
    setAbortedDraft(null)

    // Optimistic user bubble.
    const optimistic = {
      id: `tmp-${Date.now()}`,
      role: 'user',
      content: text,
      created_at: new Date().toISOString(),
      _pending: true
    }
    setMessages(prev => [...prev, optimistic])

    const controller = new AbortController()
    sendAbortRef.current = controller
    const timeoutId = setTimeout(() => controller.abort(), SEND_TIMEOUT_MS)

    try {
      // Phase 2B — route text questions through Lilli Core (/api/lilli/ask): tool-first,
      // read-only, grounded. Returns the verified-answer contract; we render the answer +
      // metadata (sources / warnings / missing facts / approval). NEVER acts on prod.
      const { data } = await api.post('/lilli/ask', { question: text, context: buildPageContext() }, {
        signal: controller.signal,
        timeout: SEND_TIMEOUT_MS + 5000,
      })
      setMessages(prev => {
        const without = prev.filter(m => m.id !== optimistic.id)
        const userMsg = { ...optimistic, _pending: false }
        const assistantMsg = {
          id: data?.auditId || `a-${Date.now()}`,
          role: 'assistant',
          content: data?.answer || 'Lilli Core returned no answer.',
          created_at: new Date().toISOString(),
          _meta: {
            sourcesUsed: Array.isArray(data?.sourcesUsed) ? data.sourcesUsed : [],
            warnings: Array.isArray(data?.warnings) ? data.warnings : [],
            missingFacts: Array.isArray(data?.missingFacts) ? data.missingFacts : [],
            requiresApproval: !!data?.requiresApproval,
            auditId: data?.auditId || null,
            confidence: data?.confidence,
            actions: Array.isArray(data?.actions) ? data.actions : [], // Phase 2D
          },
        }
        return [...without, userMsg, assistantMsg]
      })
      // If panel is closed (race: the user closed it mid-send), surface the unread pulse.
      if (!open && onUnreadChange) onUnreadChange(true)
    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== optimistic.id))
      setDraft(text) // restore the input so the user can retry
      const aborted = controller.signal.aborted || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError'
      if (aborted) {
        setAbortedDraft(text)
        setError('Request timed out.')
      } else {
        // Friendly error — do NOT silently fall back to ungrounded chat for data questions.
        setError('Lilli Core could not answer this yet. Please try again.')
      }
    } finally {
      clearTimeout(timeoutId)
      if (sendAbortRef.current === controller) sendAbortRef.current = null
      setSending(false)
    }
  }

  async function sendVoiceBlob(blob, mimeType, seconds) {
    setSending(true)
    setError(null)
    const file = new File([blob], `lilli-voice-${Date.now()}${voiceExtensionForMime(mimeType)}`, { type: mimeType || 'audio/webm' })
    const optimistic = {
      id: `tmp-voice-${Date.now()}`,
      role: 'user',
      content: 'Transcribing voice message…',
      created_at: new Date().toISOString(),
      _pending: true
    }
    setMessages(prev => [...prev, optimistic])
    try {
      const form = new FormData()
      form.append('voice', file)
      form.append('duration', String(seconds || 0))
      const { data } = await api.post('/lilli/voice', form, {
        timeout: SEND_TIMEOUT_MS + 30000,
      })
      setMessages(prev => {
        const without = prev.filter(m => m.id !== optimistic.id)
        const next = [...without]
        if (data?.userMessage) next.push(data.userMessage)
        if (data?.assistantMessage) next.push(data.assistantMessage)
        return next
      })
      if (!open && onUnreadChange) onUnreadChange(true)
    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== optimistic.id))
      setError(err?.response?.data?.error || err.message || 'Voice message failed.')
    } finally {
      setSending(false)
    }
  }

  async function startRecording() {
    if (recording || sending) return
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const { recorder, mimeType } = createVoiceRecorder(stream)
      chunksRef.current = []
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop())
        streamRef.current = null
        clearInterval(recordTimerRef.current)
        const seconds = recordSecondsRef.current
        recordSecondsRef.current = 0
        setRecordingTime(0)
        const actualMimeType = recorder.mimeType || mimeType || chunksRef.current.find((chunk) => chunk.type)?.type || ''
        const blob = new Blob(chunksRef.current, { type: actualMimeType })
        if (blob.size >= 1000) sendVoiceBlob(blob, actualMimeType, seconds)
      }
      recorder.start(250)
      recorderRef.current = recorder
      setRecording(true)
      setRecordingTime(0)
      recordTimerRef.current = setInterval(() => {
        setRecordingTime((t) => {
          if (t >= 59) { stopRecording(); return 60 }
          recordSecondsRef.current = t + 1
          return t + 1
        })
      }, 1000)
    } catch (err) {
      setError(err?.message || 'Microphone access denied.')
    }
  }

  function stopRecording() {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop()
    }
    setRecording(false)
    clearInterval(recordTimerRef.current)
  }

  function handleAbortSend() {
    sendAbortRef.current?.abort()
  }

  function handleRetryAborted() {
    if (abortedDraft) {
      setDraft(abortedDraft)
      setAbortedDraft(null)
      setError(null)
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }

  async function handleClear() {
    if (!window.confirm('Clear your Lilli chat history? This cannot be undone.')) return
    try {
      await api.delete('/lilli/messages')
      setMessages([])
      setError(null)
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to clear history')
    }
  }

  // Phase 2D — append a local assistant info bubble (no API round-trip).
  function appendInfo(text) {
    setMessages(prev => [...prev, { id: `info-${Date.now()}`, role: 'assistant', content: text, created_at: new Date().toISOString() }])
  }

  // Phase 2D — safe action dispatch. NOTHING here executes a production change: events
  // open existing reviewed UI flows (cancelable CustomEvent — if no page handles it we
  // explain instead), copy uses the clipboard, data checks re-ask a read-only question.
  async function handleLilliAction(action, message) {
    switch (action.type) {
      case 'copy_answer': {
        try { await navigator.clipboard.writeText(message.content || '') ; return 'Copied ✓' }
        catch { return 'Copy failed' }
      }
      case 'open_draft_reply': {
        const ev = new CustomEvent('lilli:openDraftReply', { detail: action.payload || {}, cancelable: true })
        const unhandled = window.dispatchEvent(ev) // true unless a listener preventDefault()ed
        if (unhandled) appendInfo('Open the email thread in the inbox first, then use Draft reply.')
        return unhandled ? null : 'Opened ✓'
      }
      case 'open_draft_quote': {
        const ev = new CustomEvent('lilli:openDraftQuote', { detail: action.payload || {}, cancelable: true })
        const unhandled = window.dispatchEvent(ev)
        if (unhandled) appendInfo('Open the email thread in the inbox first, then use Draft Quote.')
        return unhandled ? null : 'Opened ✓'
      }
      case 'prepare_wordpress_draft': {
        appendInfo('I can prepare the WordPress draft content next — ask me for the text you need (for example: “write the updated description for this villa”). Oliver must paste it into WordPress manually; I never write to WordPress.')
        return null
      }
      case 'run_data_quality_check': {
        await sendQuestion('Run a read-only data quality check for this villa.')
        return null
      }
      case 'suggest_follow_up': {
        appendInfo(action.payload?.text || 'Consider a follow-up message with this contact — I can suggest wording if you ask.')
        return null
      }
      case 'open_related_record': {
        const route = action.payload?.route
        if (typeof route === 'string' && /^\/[a-z0-9/_-]*$/i.test(route)) window.location.assign(route)
        return null
      }
      default:
        return null
    }
  }

  function handleKeyDown(e) {
    // Enter sends, Shift+Enter inserts newline (consistent with operator chat).
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  if (!open) return null

  return (
    <div
      className="lilli-chat-panel fixed bottom-24 right-4 sm:right-6 z-[56] w-[min(380px,calc(100vw-2rem))] h-[min(560px,calc(100vh-8rem))] flex flex-col rounded-2xl shadow-2xl overflow-hidden bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700"
      role="dialog"
      aria-label="Lilli internal assistant"
    >
      <style>{`
        @keyframes lilli-pop {
          0%   { opacity: 0; transform: translateY(8px) scale(.98); }
          100% { opacity: 1; transform: translateY(0)   scale(1); }
        }
        .lilli-chat-panel {
          animation: lilli-pop 180ms ease-out;
        }
        @media (prefers-reduced-motion: reduce) {
          .lilli-chat-panel {
            animation: none;
          }
        }
      `}</style>

      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 text-white"
        style={{ background: DARK }}
      >
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-[#1e2a2e]"
            style={{ background: COPPER }}
          >
            <Sparkles size={16} />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold tracking-wide">Lilli</div>
            {/* Phase 2C — compact context hint: what Lilli currently "sees" */}
            {ctxLabel ? (
              <div className="text-[11px] flex items-center gap-1">
                <span className="opacity-60">Context:</span>
                <span className="font-medium truncate max-w-[180px]" style={{ color: COPPER }}>{ctxLabel}</span>
              </div>
            ) : (
              <div className="text-[11px] opacity-70">Internal · operations assistant</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleClear}
            title="Clear chat"
            className="p-1.5 rounded hover:bg-white/10 transition"
          >
            <Trash2 size={16} />
          </button>
          <button
            onClick={onClose}
            title="Close"
            className="p-1.5 rounded hover:bg-white/10 transition"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto px-4 py-3 bg-slate-50 dark:bg-slate-950 space-y-3"
      >
        {loading && (
          <div className="flex justify-center py-6">
            <Loader2 size={18} className="animate-spin text-slate-400" />
          </div>
        )}

        {!loading && messages.length === 0 && !error && (
          <div className="text-center text-xs text-slate-500 dark:text-slate-400 pt-8 px-2">
            Ask Lilli about villas, rates, bookings, owners, availability, or commissions.
            <br /><br />
            <span className="opacity-70">e.g. "What's the rate for Villa Magnifico in July?"</span>
          </div>
        )}

        {messages.map(m => (
          <div
            key={m.id}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                m.role === 'user'
                  ? 'bg-[#1e2a2e] text-white rounded-br-sm'
                  : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-bl-sm'
              } ${m._pending ? 'opacity-60' : ''}`}
            >
              {renderLilliMarkdown(m.content)}
              {m.role !== 'user' && m._meta && <LilliMeta meta={m._meta} />}
              {m.role !== 'user' && m._meta && (
                <LilliActions actions={m._meta.actions} message={m} onAction={handleLilliAction} />
              )}
              <div className={`text-[10px] mt-1 opacity-60 ${m.role === 'user' ? 'text-white/80' : 'text-slate-500'}`}>
                {formatTime(m.created_at)}
              </div>
            </div>
          </div>
        ))}

        {sending && (
          <div className="flex justify-start">
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl rounded-bl-sm px-3 py-2 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" />
              Lilli is checking…
              <button
                type="button"
                onClick={handleAbortSend}
                className="ml-1 text-[#1e2a2e] dark:text-[#cfa190] underline underline-offset-2"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 rounded-lg px-3 py-2">
            {error}
            {abortedDraft && (
              <button
                type="button"
                onClick={handleRetryAborted}
                className="ml-2 font-medium underline underline-offset-2"
              >
                Request timed out — retry?
              </button>
            )}
          </div>
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={handleSend}
        className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 flex items-end gap-2"
      >
        <textarea
          ref={inputRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
          placeholder="Ask Lilli…"
          className="flex-1 resize-none max-h-32 px-3 py-2 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#cfa190] text-slate-800 dark:text-slate-100"
          disabled={sending}
        />
        <button
          type="button"
          onMouseDown={startRecording}
          onMouseUp={stopRecording}
          onMouseLeave={stopRecording}
          onTouchStart={(e) => { e.preventDefault(); startRecording() }}
          onTouchEnd={stopRecording}
          disabled={sending}
          className="w-10 h-10 rounded-xl flex items-center justify-center text-[#1e2a2e] disabled:opacity-40 disabled:cursor-not-allowed transition border border-slate-200 dark:border-slate-700"
          title={recording ? `Recording ${recordingTime}s` : 'Hold to record voice message'}
          aria-label={recording ? 'Recording voice message' : 'Record voice message'}
        >
          <Mic size={16} className={recording ? 'text-red-500 animate-pulse' : ''} />
        </button>
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          className="w-10 h-10 rounded-xl flex items-center justify-center text-[#1e2a2e] disabled:opacity-40 disabled:cursor-not-allowed transition"
          style={{ background: COPPER }}
          title="Send (Enter)"
        >
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </form>
    </div>
  )
}
